import { applyVisualDesignerTemplateBlocks } from './visualDesignerTemplate';
import { stripTrailingEmptyPdfPagesHtml } from './prepareExportHtml';

type AnyRecord = Record<string, any>;

type CleanupMode = 'default' | 'strict';

type RenderTemplateArgs = {
  templateHtml: string;
  templateData: AnyRecord;
  cleanupMode?: CleanupMode;
};

function normalizeDescriptionTemplateMarkup(templateHtml: string): string {
  return String(templateHtml || '')
    .replace(/<p[^>]*>\s*\{\{description\}\}\s*<\/p>/gi, '{{description}}')
    .replace(/<div[^>]*>\s*\{\{description\}\}\s*<\/div>/gi, '{{description}}');
}

function ensureVisualDesignerV2Fields(templateData: AnyRecord): AnyRecord {
  const next = { ...templateData };
  const items = Array.isArray(next.visualDesignerItems) ? next.visualDesignerItems : [];
  if (!Array.isArray(next.visualDesignerItemsV2)) {
    next.visualDesignerItemsV2 = items;
  }
  if (typeof next.hasVisualDesignerItemsV2 !== 'boolean') {
    next.hasVisualDesignerItemsV2 = items.some((it: any) => {
      if (!!String(it?.imageUrlPresentationExternal || '').trim()) return true;
      if (!!String(it?.imageUrlPresentationInternal || '').trim()) return true;
      if (!!String(it?.imageUrlDesignExternal || '').trim()) return true;
      if (!!String(it?.imageUrlDesignInternal || '').trim()) return true;
      return !!String(it?.imageUrlInternal || '').trim();
    });
  }
  return next;
}

function applySimpleVariables(template: string, templateData: AnyRecord): string {
  let result = template;
  Object.entries(templateData).forEach(([key, value]) => {
    if (typeof value === 'string') {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    }
  });
  return result;
}

function applyConditionalBlocks(template: string, templateData: AnyRecord): string {
  const truthyKeys = [
    'logoPath',
    'companyAddress',
    'companyPhone',
    'companyEmail',
    'companyVatNumber',
    'paymentDetails',
    'bankName',
    'bankAccountName',
    'bankAccountNumber',
    'bankSortCode',
    'bankIban',
    'bankSwift',
    'customerTitle',
    'customerAddress',
    'description',
    'showFees',
    'vatEnabled',
    'depositOnly',
    'outstandingOnly',
    'stageOnly',
    'hasLineItems',
    'hasVisualDesignerItems',
    'hasVisualDesignerItemsV2',
    'hasDeposit',
    'noDeposit',
    'depositDisabled',
    'isDepositInvoice',
    'isBalanceInvoice',
    'isVariationInvoice',
    'depositPercentage',
    'depositPct',
    'depositNarrative',
    'depositClearedFundsNote',
    'depositInvoiceReference',
    'noVat',
    'labourDays',
    'labourHours',
    'labourDaysText',
    'labourHoursText',
    'dueDate',
    'paymentTermsLabel',
    'paymentTermsWording',
    // Account invoices (Finance → Invoicing)
    'hasCisDeduction',
  ];
  const inverseKeys = [
    'paymentDetails',
    'vatEnabled',
    'depositOnly',
    'outstandingOnly',
    'stageOnly',
    'hasDeposit',
    'isDepositInvoice',
    'isBalanceInvoice',
    'isVariationInvoice',
    'depositInvoiceReference',
  ];

  let result = template;
  truthyKeys.forEach((key) => {
    result = result.replace(new RegExp(`\\{\\{#${key}\\}\\}([\\s\\S]*?)\\{\\{\\/${key}\\}\\}`, 'g'), templateData[key] ? '$1' : '');
  });
  inverseKeys.forEach((key) => {
    result = result.replace(new RegExp(`\\{\\{\\^${key}\\}\\}([\\s\\S]*?)\\{\\{\\/${key}\\}\\}`, 'g'), !templateData[key] ? '$1' : '');
  });
  return result;
}

function applyLineItemsBlock(template: string, templateData: AnyRecord): string {
  if (!templateData.hasLineItems) return template;
  const lineItems = Array.isArray(templateData.lineItems) ? templateData.lineItems : [];
  const lineItemsHtml = lineItems.map((item: any) => `
    <tr>
      <td><strong>${item.name}</strong><br />
      ${item.description ? `<span class="item-description">${item.description}</span>` : ''}</td>
      <td>${item.quantity}</td>
      <td>${item.unit}</td>
      <td>${item.unitPrice}</td>
      <td>${item.lineTotal}</td>
    </tr>
  `).join('');
  return template.replace(/\{\{#lineItems\}([\s\S]*?)\{\{\/lineItems\}\}/g, lineItemsHtml);
}

function cleanupRenderedTemplate(template: string, mode: CleanupMode): string {
  let result = template
    .replace(/<p>\s*[{}]+\s*<\/p>/g, '')
    .replace(/<div>\s*[{}]+\s*<\/div>/g, '')
    .replace(/<p>\s*[{})\s]*<\/p>/g, '')
    .replace(/<div>\s*[{})\s]*<\/div>/g, '')
    .replace(/(?<!\w)[{}\s]*(?=\s*<)/g, '')
    .replace(/>\s*[{}\s]*(?=\s*$)/g, '>')
    .replace(/\s*[{}]\s*(?=\s*\()/g, '')
    .replace(/[{}]\s*(?=\s*\(incl\.\s*VAT\))/g, '');

  // Remove empty blocks, but preserve intentional page-break markers.
  result = result.replace(
    /<(p|div)([^>]*)>\s*(?:&nbsp;|<br\s*\/?>|\s)*<\/\1>/gi,
    (_full, _tag, attrs) => {
      const attrText = String(attrs || '').toLowerCase();
      const isPageBreakMarker =
        /page-break-before\s*:\s*always/.test(attrText) ||
        /break-before\s*:\s*page/.test(attrText);
      return isPageBreakMarker ? _full : '';
    }
  );

  if (mode === 'strict') {
    result = result
      .replace(/\{\{[^}]+\}\}/g, '')
      .replace(/[{}]/g, '')
      .replace(/([.!?:;])\s+\)\s+/g, '$1 ')
      .replace(/([.!?:;])\s+\)(?=[A-Z])/g, '$1 ');
  } else {
    result = result.replace(/[{}]+/g, '');
  }

  // Drop page-breaks that only have empty nodes / wrappers / footer after them.
  result = stripTrailingEmptyPdfPagesHtml(result);

  // Collapse accidental duplicate consecutive paragraphs (same visible text),
  // which can happen via template edits/copying and is most obvious in sign-off lines.
  result = result.replace(
    /<p([^>]*)>\s*([^<][\s\S]*?)\s*<\/p>\s*<p([^>]*)>\s*\2\s*<\/p>/gi,
    '<p$1>$2</p>'
  );

  return result
    .replace(/\s+\./g, '.')
    .replace(/\.\s*\./g, '.');
}

function isDepositInvoiceTemplate(templateData: AnyRecord): boolean {
  return templateData.isDepositInvoice === true
    || String(templateData.invoiceRole || '').trim().toLowerCase() === 'deposit';
}

function ensureInvoicePaymentSchedule(html: string, templateData: AnyRecord): string {
  if (String(templateData.documentType || '').toLowerCase() !== 'invoice') return html;
  if (isDepositInvoiceTemplate(templateData)) return html;
  const dueDate = String(templateData.dueDate || '').trim();
  const termsLabel = String(templateData.paymentTermsLabel || '').trim();
  const wording = String(templateData.paymentTermsWording || '').trim();
  if (!dueDate && !termsLabel && !wording) return html;
  const hasDue = !!dueDate && html.includes(dueDate);
  const hasTerms = (!!termsLabel && html.includes(termsLabel)) || (!!wording && html.includes(wording));
  if (hasDue && hasTerms) return html;

  const block = `<div class="invoice-payment-schedule">${
    [
      termsLabel ? `<div class="date">Payment terms: ${termsLabel}</div>` : '',
      dueDate ? `<div class="date">Due date: ${dueDate}</div>` : '',
      wording ? `<div class="payment-terms">${wording}</div>` : '',
    ].filter(Boolean).join('')
  }</div>`;

  const dateBlock = html.match(/<div class="date">[\s\S]*?<\/div>/i);
  if (dateBlock && dateBlock.index != null) {
    const at = dateBlock.index + dateBlock[0].length;
    return `${html.slice(0, at)}${block}${html.slice(at)}`;
  }
  return placeScheduleInFreeformTemplate(html, {
    documentDate: String(templateData.documentDate || '').trim(),
    dueDate: hasDue ? '' : dueDate,
    termsLabel,
    wording: hasTerms ? '' : wording,
    fallbackBlock: block,
  });
}

/**
 * Hand-edited templates have no `.date` block: put the due date under the
 * paragraph that shows the invoice date, and the wording above the payment
 * details, so neither lands above the title.
 */
function placeScheduleInFreeformTemplate(html: string, args: {
  documentDate: string;
  dueDate: string;
  termsLabel: string;
  wording: string;
  fallbackBlock: string;
}): string {
  const escaped = args.documentDate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const dateParagraph = escaped
    ? html.match(new RegExp(`<p\\b[^>]*>(?:(?!<\\/p>)[\\s\\S])*?${escaped}[\\s\\S]*?<\\/p>`, 'i'))
    : null;
  if (!dateParagraph || dateParagraph.index == null) return `${args.fallbackBlock}${html}`;

  let result = html;
  if (args.dueDate) {
    const align = dateParagraph[0].match(/^<p\b[^>]*text-align:\s*(left|right|center)/i)?.[1] || 'right';
    const terms = args.termsLabel ? ` (${args.termsLabel})` : '';
    const dueLine = `<p class="invoice-due-date" style="text-align: ${align};"><span style="font-size: 10pt;">Due date: ${args.dueDate}${terms}</span></p>`;
    const at = dateParagraph.index + dateParagraph[0].length;
    result = `${result.slice(0, at)}${dueLine}${result.slice(at)}`;
  }
  if (args.wording) {
    const wordingLine = `<p class="payment-terms"><span style="font-size: 10pt;">${args.wording}</span></p>`;
    const paymentIntro = result.match(/<p\b[^>]*>(?:(?!<\/p>)[\s\S])*?make payment[\s\S]*?<\/p>/i);
    if (paymentIntro && paymentIntro.index != null) {
      result = `${result.slice(0, paymentIntro.index)}${wordingLine}${result.slice(paymentIntro.index)}`;
    } else {
      const afterDate = result.indexOf('</p>', dateParagraph.index) + 4;
      const dueEnd = args.dueDate ? result.indexOf('</p>', afterDate) + 4 : afterDate;
      result = `${result.slice(0, dueEnd)}${wordingLine}${result.slice(dueEnd)}`;
    }
  }
  return result;
}

function ensureDepositInvoiceReferenceNote(html: string, templateData: AnyRecord): string {
  if (!templateData.depositInvoiceReference) return html;
  const narrative = String(templateData.depositAlreadyInvoicedNarrative || '').trim();
  if (!narrative) return html;
  if (html.includes(narrative)) return html;
  const note = `<p class="meta-note">${narrative}</p>`;
  const lastTable = html.lastIndexOf('</table>');
  if (lastTable === -1) return `${html}${note}`;
  return `${html.slice(0, lastTable + 8)}${note}${html.slice(lastTable + 8)}`;
}

export function renderDocumentTemplate(args: RenderTemplateArgs): string {
  const cleanupMode: CleanupMode = args.cleanupMode || 'default';
  const templateData = ensureVisualDesignerV2Fields(args.templateData || {});
  let renderedTemplate = normalizeDescriptionTemplateMarkup(args.templateHtml || '');
  renderedTemplate = applySimpleVariables(renderedTemplate, templateData);
  renderedTemplate = applyConditionalBlocks(renderedTemplate, templateData);
  renderedTemplate = applyLineItemsBlock(renderedTemplate, templateData);
  renderedTemplate = applyVisualDesignerTemplateBlocks(renderedTemplate, templateData);
  renderedTemplate = cleanupRenderedTemplate(renderedTemplate, cleanupMode);
  renderedTemplate = ensureInvoicePaymentSchedule(renderedTemplate, templateData);
  renderedTemplate = ensureDepositInvoiceReferenceNote(renderedTemplate, templateData);
  return renderedTemplate;
}
